import {
  Box,
  Card,
  CardBody,
  Heading,
  Text,
  VStack,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Link,
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { HelpCircle } from 'lucide-react'

const FAQ_ITEMS = [
  {
    q: 'What is the My Requests page?',
    a: 'The My Requests page is where you view, create, and manage your support ticket requests. You can submit new requests, save drafts, edit drafts before submitting, and track the status of your requests.',
  },
  {
    q: 'How do I create a new request?',
    a: 'Click the "Create Request" button at the top of the My Requests page. Fill in the form: select a Service type, describe the issue, add contact details, and attachments if needed. You can submit immediately or save as draft to finish later.',
  },
  {
    q: 'What is the difference between saving a draft and submitting?',
    a: 'Saving as draft stores your request without sending it to the support team. You can edit drafts anytime. Once you submit a request, it is sent and can no longer be edited from this page; only drafts can be edited.',
  },
  {
    q: 'How do I search or filter requests?',
    a: 'Use the search box to find requests by request number, description, or contact details. You can filter by Status using the filter on the Status column. Use the "All" and "Trash" tabs to switch between active requests and deleted (archived) ones.',
  },
  {
    q: 'What do the statuses mean?',
    a: 'Draft = not yet submitted. New = just submitted. Pending = waiting for action. In Progress = being worked on. Resolved = completed. Closed = request closed. Cancelled = request was cancelled.',
  },
  {
    q: 'Can I attach files to a request?',
    a: 'Yes. When creating or editing a request, use the attachments area to add files (e.g. screenshots, documents). Supported file types and size limits apply.',
  },
  {
    q: 'How do I edit or delete a request?',
    a: 'Only drafts can be edited: click the Edit action on the row to open the form. To delete a request, use the Delete (trash) action; the request is moved to Trash. You can view trashed requests in the Trash tab and restore or permanently delete them from there.',
  },
]

export default function HelpCenterPage() {
  const colors = useThemeColors()

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="auto">
        <VStack align="stretch" spacing={6} maxW="900px" mx="auto">
          <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack spacing={3} align="center">
                  <Box color="blue.500">
                    <HelpCircle size={32} />
                  </Box>
                  <Box>
                    <Heading size="lg" color={colors.text}>
                      Help Center
                    </Heading>
                    <Text color={colors.textSecondary} fontSize="sm">
                      Frequently asked questions about using the portal
                    </Text>
                  </Box>
                </HStack>
                <Text color={colors.text} fontSize="sm">
                  This guide covers how to use the <strong>My Requests</strong> page to create and manage your support requests.{' '}
                  <Link as={RouterLink} to="/my-request" color="blue.500">
                    Go to My Requests
                  </Link>
                </Text>
              </VStack>
            </CardBody>
          </Card>

          <Heading size="md" color={colors.text} alignSelf="start">
            Requests management (My Requests)
          </Heading>

          <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
            <CardBody py={0} px={0}>
              <Accordion allowMultiple reduceMotion>
                {FAQ_ITEMS.map((item, index) => (
                  <AccordionItem key={index} borderColor={colors.border}>
                    <AccordionButton
                      py={4}
                      px={6}
                      _expanded={{ bg: colors.bg }}
                      _hover={{ bg: colors.bg }}
                    >
                      <Box flex="1" textAlign="left" fontWeight="medium" color={colors.text}>
                        {item.q}
                      </Box>
                      <AccordionIcon color={colors.textSecondary} />
                    </AccordionButton>
                    <AccordionPanel pb={4} px={6} pt={0} color={colors.textSecondary} fontSize="sm">
                      {item.a}
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
